import "bootstrap/dist/css/bootstrap.css";
import React, {useContext, useEffect, useRef, useState} from "react";
import { useTranslation } from "react-i18next";
import { ResponseApiCalls } from "../../apis/ResponseApiCalls";
import { useLocalStorageNew } from "../../hooks/useLocalStorageNew";
import { ReactComponent as ARROW_UP } from "./images/arrow-up.svg";
import { ReactComponent as ARROW_DOWN } from "./images/arrow-down.svg";
import { ReactComponent as EXPAND_ICON } from "./images/expand-icon.svg";
import { ReactComponent as BACK_ICON } from "./images/left-arrow-purple.svg";

import { YuJaGlobalState, useLoading } from "../../utils/LoadingContext";
import {
    BLANK_REGEX,
    BLANK_REGEX_NEW,
    QUESTION_TYPES,
    SCREEN_TYPE,
    UNANSWERED,
    WORD_CLOUD_DATA
} from "../../utils/constants";
import {
    InitializeQuestionResultCount,
    hasWC,
    newPageLog,
    questionHasWeightage,
    getQuestionWeightage,
    getSafeURL
} from "../../utils/helpers";
import {questionCodeToName, questionCodeToSVG} from "../../utils/questionUtils";
import "./styles.css";
import classes from "./ClassResult.module.css";
import Skeleton, {SkeletonTheme} from "react-loading-skeleton";
import ImageViewer from "./ImageViewer";
import RenderYourResponse from "./RenderYourResponse";
import RenderCorrectAnswer from "./RenderCorrectAnswer";
import RenderAllResponse from "./RenderAllResponse";
import useWindowDimensions from "../../hooks/useWindowDimensions";
import reactStringReplace from "react-string-replace";
import {YuJaTooltip} from "../../components/standardization/YuJaTooltip";
import {LatexInline} from "../../components/Tiptap/latexParser";
import {Button} from "@base-ui/react/button";

export default function ClassResultAllMerged({ currentPoll, websocketState,  pollInfo, setCountdownContent, setShowClassResult}) {
    const { t } = useTranslation();
    const {width: windowWidth, type: windowType} = useWindowDimensions();
    const { questions, rawQueResponses = [], timeLimit } = currentPoll;
    const { pollKey, uniqueCode, pollTitle, showAnswerOnViewer} = pollInfo;
    const { getAllClassResult} = ResponseApiCalls();
    const { setLoading } = useLoading();
    const [questionsData, setQuestionsData] = useState([]);
    const [setWordCloudData,] = useLocalStorageNew(WORD_CLOUD_DATA, {});
    const { isOnline } = useContext(YuJaGlobalState);
    const [selectedQuestion, setSelectedQuestion] = useState(questions[0]);
    // const [participantCount, setParticipantCount] = useState("-");
    // const [responsesCount, setResponsesCount] = useState("-");
    // const [completionRate, setCompletionRate] = useState("-");
    const [questionData, setQuestionData] = useState(() => {
        return {
            ...questions[0],
            questionType: questions[0].questionType,
            queTitle: questions[0].queTitle,
            questionResults: InitializeQuestionResultCount(questions[0]),
            rawResults: {},
            serialNo: questions[0].serialNo,
            imageURL: questions[0].imageURL,
            image: questions[0].image,
            suffix: questions[0].suffix,
            directLinkEvp: questions[0].directLinkEvp,
            blankRegex: questions[0].hasBlankRestriction ? BLANK_REGEX_NEW : BLANK_REGEX
        };
    });
    const [dataLoading, setDataLoading] = useState(false);
    // const [, getSession] = useLocalStorageNew("session", {});
    const [questionListOpen, setQuestionListOpen] = useState(false);
    const [pollPanelOpen, setPollPanelOpen] = useState(false);
    const pollPanelRef = useRef();
    const questionListContainerRef = useRef();

    useEffect(() => {
        newPageLog("ClassResultAllMerged.js");

        setDataLoading(true);
        getQuestionsData().finally(() => {
            setDataLoading(false);
        });

    }, []);

    useEffect(() => {
        if (hasWC(questions)) setWordCloudData({});
    }, [questions]);

    function removeUnanswered(result) {
        let newResult = [];
        if (result == null) {
            return newResult;
        }
        result.forEach((r) => {
            if (r.ans !== UNANSWERED) {
                newResult.push({ ...r });
            }
        });
        return newResult;
    }

    async function getQuestionsData() {
        setLoading(true);
        const results = await getAllClassResult(pollKey, uniqueCode, questions);
        let tempQuestionsData = [];
        for (const result of results) {
            let serialNo = result.serialNo;
            const que = questions.find(({ serialNo: no }) => parseInt(no) === parseInt(serialNo));
            let questionType = que.questionType;
            let queTitle = que.queTitle;
            let MCSSelements = [];
            if (questionType === QUESTION_TYPES.MCSS.name) {
                for (let key in que.optionsMap) {
                    MCSSelements.push(que.optionsMap[key]);
                }
            }
            let MCSSnum = MCSSelements.length;
            let questionImageURL;
            let isVideo = false;
            if (que.directLinkEvp) {
                questionImageURL = que.directLinkEvp;
                isVideo = true;
            } else {
                questionImageURL = que.imageURL;
                isVideo = false;
            }

            let questionData = {
                ...que,
                questionType: questionType,
                queTitle: queTitle,
                gradeEnabled: questionHasWeightage(que),
                questionResults: result.questionResults,
                serialNo: serialNo,
                MCSSnum: MCSSnum,
                questionImageURL: questionImageURL,
                isVideo: isVideo,
                rawResponse: result.rawResponse
            };
            tempQuestionsData.push(questionData);
        }

        tempQuestionsData.forEach(item => {
            item.unansweredCount = getUnanswered(item.questionResults);
        });

        tempQuestionsData.forEach(item => {
            item.questionResults = removeUnanswered(item.questionResults);
        });

        if (JSON.stringify(questionsData) !== JSON.stringify(tempQuestionsData)) {
            setQuestionsData(tempQuestionsData);
        }

        setLoading(false);
    }

    const getUnanswered = (result) => {
        if (result == null) {
            return 0;
        }

        let unansweredCount = 0;
        result.forEach((r) => {
            if (r.ans === UNANSWERED) {
                unansweredCount = r.people;
            }
        });
        return unansweredCount;
    }

    useEffect(() => {
        if (!dataLoading && questionsData && questionsData.length !== 0) {
            let serialNo = selectedQuestion.serialNo;
            const queData = questionsData.find(({ serialNo: no }) => parseInt(no) === parseInt(serialNo));
            setQuestionData(queData);
        }
    }, [questionsData, selectedQuestion, dataLoading]);



    useEffect(() => {
        const handleGlobalClick = (event) => {
            if (questionListOpen && !!questionListContainerRef.current && !!questionListContainerRef.current
                && !questionListContainerRef.current.contains(event.target)
            ) {
                setQuestionListOpen(false);
            }
        };

        if (questionListOpen) {
            document.addEventListener('click', handleGlobalClick, true);
        }
        return () => {
            document.removeEventListener('click', handleGlobalClick)
        };
    }, [questionListOpen]);

    useEffect(() => {
        const handleGlobalClick = (event) => {
            if (pollPanelOpen && !!pollPanelRef.current && !!pollPanelRef.current
                && !pollPanelRef.current.contains(event.target)
            ) {
                setPollPanelOpen(false);
            }
        };

        if (pollPanelOpen) {
            document.addEventListener('click', handleGlobalClick, true);
        }
        return () => {
            document.removeEventListener('click', handleGlobalClick)
        };
    }, [pollPanelOpen]);

    const updateTopNavContent = () => {
        if (!setCountdownContent) {
            return;
        }
        if (windowType === SCREEN_TYPE.MOBILE) {
            setCountdownContent(
                <div style={{height: "100%", display: "flex", justifyContent: "center", alignItems: "center", gap: 5, padding: "0 20px"}}>
                    <span className={classes.navBarTitle}>{pollTitle}</span>
                    {!pollPanelOpen &&
                        <ARROW_DOWN style={{cursor: "pointer", flexShrink: 0}} onClick={() => setPollPanelOpen(true)}/>
                    }

                    {!!pollPanelOpen &&
                        <ARROW_UP style={{cursor: "pointer", flexShrink: 0}}/>
                    }

                    {!!pollPanelOpen &&
                        <div ref={pollPanelRef} className={classes.pollPanelContainer}>
                            <span className={classes.cardTitle}>{pollTitle}</span>
                            <div style={{display: "flex", gap: 5}}>
                               <span className={classes.badge} style={{backgroundColor: "#F8DCDB"}}>
                                 {questions.length} {t("table.questionsHeader")}
                               </span>
                                <span className={classes.badge} style={{backgroundColor: "#D3DDF2"}}>
                                  {questions.reduce(function (result, q) {
                                      return result + getQuestionWeightage(q);
                                  }, 0)} {t("poll.pointsText")}
                               </span>
                                {!!timeLimit &&
                                    <span className={classes.badge} style={{backgroundColor: "#F9E4F5"}}>
                                        {timeLimit / 60} {t("common.minutesAbbr")}
                                   </span>
                                }
                            </div>
                        </div>
                    }
                </div>
            );
        } else {
            setCountdownContent(null);
        }
    }


    useEffect(() => {
        updateTopNavContent();
    }, [setCountdownContent, pollTitle, pollPanelOpen, windowType]);


    const renderTitle = () => {
        if (!!dataLoading) {
            return (
                <SkeletonTheme color="rgba(237, 237, 237, 1)" highlightColor="lightgrey">
                    <Skeleton borderRadius={4} height={30} width={"min(80vw, 500px)"}/>
                </SkeletonTheme>
            );
        }

        if (questionData.questionType !== QUESTION_TYPES.FITB.name) {
            return <LatexInline>{questionData.queTitle}</LatexInline>;
        }

        let blankNameArr = Object.keys(questionData.correctAnswers).map(str => `\\(${str}\\)`);
        const regex = new RegExp(`(${blankNameArr.join("|")})`, "g");

        return (
            <span style={{marginRight: 5, lineHeight: "32px"}}>
               {reactStringReplace(questionData?.queTitle, regex, (blank, i) => {

                   const match = !!blank ? blank.match(/\(([^)]+)\)/) : null;
                   if (!match) {
                       return "";
                   }
                   const blankName = match[1];

                   return (
                       <input
                           key={i}
                           id={blankName}
                           className={classes.FITBInputBox}
                           disabled={true}
                           placeholder={t('poll.saPlaceholder')}
                           type={"text"}
                           style={{backgroundColor: "#FFF"}}
                       />
                   );
               })}
           </span>
        );
    }

    return (
        <div className={`${classes.parentContainer} ${windowType === SCREEN_TYPE.MOBILE ? classes.mobile : ""}`}>
            {windowType !== SCREEN_TYPE.MOBILE &&
                <div className={classes.pollTitleContainer} style={{gap: 20}}>
                    <Button
                        style={{width: 70, cursor: "pointer", flexShrink: 0}}
                        onClick={() => setShowClassResult(false)}
                    >
                        <BACK_ICON/> {t("common.back")}
                    </Button>
                    <div style={{flex: 1}}/>
                    <span className={classes.pollTitleContainerTitle}>
                        {!!pollTitle &&
                            <span>{pollTitle}</span>
                        }
                        {!pollTitle &&
                            <SkeletonTheme color="rgba(237, 237, 237, 1)" highlightColor="lightgrey">
                                <Skeleton width={windowWidth * 0.7} height={40} borderRadius={6}/>
                            </SkeletonTheme>
                        }
                    </span>
                    <div style={{flex: 1}}/>
                    <span style={{width: 70}}/>
                </div>
            }


            <div className={`${classes.card} ${windowType === SCREEN_TYPE.MOBILE ? classes.mobile : ""}`}>
                <div
                    ref={questionListContainerRef}
                    className={`${classes.questionListContainer} 
                    ${windowType === SCREEN_TYPE.TABLET ? classes.tablet : ""} 
                    ${windowType === SCREEN_TYPE.MOBILE ? classes.mobile : ""}
                    ${questionListOpen ? classes.opened : ""}`}
                    style={{width: windowType === SCREEN_TYPE.MOBILE ? (questionListOpen ? 88 : 0) : undefined}}
                >
                    {!!questions && (questions.map((que, index) => (
                        <div
                            key={index}
                            tabIndex={0}
                            className={`${classes.questionListItem} 
                            ${selectedQuestion.serialNo === que.serialNo ? classes.active : ""}
                            ${windowType === SCREEN_TYPE.MOBILE ? classes.mobile : ""}`}
                            onClick={() => {
                                setDataLoading(true)
                                getQuestionsData().finally(() => {
                                    setDataLoading(false);
                                });
                                setSelectedQuestion(que);
                                setQuestionListOpen(false);
                            }}
                        >
                            <YuJaTooltip
                                placement={windowType === SCREEN_TYPE.MOBILE ? "left" : "right"}
                                text={t(QUESTION_TYPES[que.questionType].labelKey)}
                            >
                                <span className={classes.questionListQuestionTypeCircle}>
                                   {questionCodeToSVG(que.questionType, true, 18)}
                                </span>
                            </YuJaTooltip>


                            <span className={classes.questionListItemText}>
                                {windowType === SCREEN_TYPE.DESKTOP ? t("poll.questionNumber", {number: que.serialNo}) : t("poll.questionNumberShort", {number: que.serialNo})}
                            </span>
                        </div>

                    )))}
                </div>

                <div className={classes.mainContainer}>
                    <div className={classes.mainContainerRow}>
                        <span style={{fontSize: 18, fontWeight: "500"}}>
                            {t('poll.questionLabelWithSpace')} {selectedQuestion?.serialNo}
                        </span>
                        {questionHasWeightage(selectedQuestion) &&
                            <span
                                className={classes.badge}
                                style={{width: "fit-content", padding: "0 10px", backgroundColor: "#E4E3F6"}}
                            >
                                {getQuestionWeightage(selectedQuestion)} {t('poll.pointsText')}
                            </span>
                        }

                        <span style={{flex: 1}}/>

                        {windowType === SCREEN_TYPE.MOBILE &&
                            <Button
                                className={classes.expandIcon}
                                style={{marginLeft: 5}}
                                onClick={() => setQuestionListOpen(true)}
                            >
                              <EXPAND_ICON/>
                            </Button>
                        }
                    </div>
                    <div className={classes.mainContainerRow}>
                      <span style={{color: "#6B6B6B"}}>
                        {questionCodeToName(selectedQuestion?.questionType)}
                      </span>
                    </div>

                    <div
                        className={classes.mainContainerRow}
                        style={{
                            flexDirection: windowType === SCREEN_TYPE.MOBILE ? "column" : undefined,
                            alignItems: "start"
                        }}
                    >
                        <span>{renderTitle()}</span>
                        <span style={{flex: 1}}/>

                        {!!selectedQuestion?.imageURL && !selectedQuestion?.directLinkEvp &&
                            <ImageViewer
                                size={150}
                                alt={selectedQuestion?.imageAlt}
                                imageURL={selectedQuestion.imageURL}
                                styles={{alignSelf: windowType === SCREEN_TYPE.MOBILE ? "center" : undefined}}
                            />
                        }
                        {!selectedQuestion?.imageURL && !!getSafeURL(selectedQuestion?.directLinkEvp) &&
                            <iframe allowFullScreen style={{width: 270, height: 270, alignSelf: "center"}}
                                    src={getSafeURL(selectedQuestion.directLinkEvp)}/>
                        }
                    </div>

                    <RenderYourResponse
                        curUserResponses={rawQueResponses}
                        showAnswerOnViewer={showAnswerOnViewer}
                        questionData={questionData}
                        dataLoading={dataLoading}
                    />
                    <RenderCorrectAnswer
                        questionData={questionData}
                        curUserResponses={rawQueResponses}
                        showAnswerOnViewer={showAnswerOnViewer}
                        dataLoading={dataLoading}
                    />

                    <RenderAllResponse
                        pollKey={pollKey}
                        uniqueCode={uniqueCode}
                        questionData={questionData}
                        curUserResponses={rawQueResponses}
                        pollResults={questionsData}
                        dataLoading={dataLoading}
                        showAnswerOnViewer={showAnswerOnViewer}
                    />

                </div>
            </div>
        </div>
    );
}
                        // <YuJaButton
                        //     style={{position: "fixed", right: 0,
                        //         transform: "translate(50%, 50%) rotate(-90deg)",
                        //         transformOrigin: "bottom",
                        //         top: "calc(80% - 90px)"}}
                        //     type={"secondary"}
                        //     onClick={() => setFeedBackModalShow(true)}
                        // >
                        //     {FEEDBACK_BUTTON_TEXT}
                        // </YuJaButton>
